import * as React from 'react';
import styled, { css } from 'styled-components';
import * as grid from 'view/styles/grid';
import * as zIndex from 'view/styles/z-index';
import * as mixins from 'view/styles/mixins';
import * as bp from 'view/styles/breakpoints';
import { HtmlButton } from '../analytics/html-button';
import { token } from '@atlaskit/tokens';
import { colors } from '@atlaskit/theme';

export const TRANSFORM_OPEN_TRANSITION_MS = 300;
const TRANSFORM_CLOSE_TRANSITION_MS = TRANSFORM_OPEN_TRANSITION_MS * 0.666;
const BACKGROUND_OPEN_TRANSITION_MS = TRANSFORM_OPEN_TRANSITION_MS * 0.125;
const BACKGROUND_CLOSE_TRANSITION_MS = TRANSFORM_OPEN_TRANSITION_MS * 1.5;
const BUTTON_OPACITY_TRANSITION_MS = 50;
const BUTTON_TRANSITION_DELAY_MS = TRANSFORM_CLOSE_TRANSITION_MS - BUTTON_OPACITY_TRANSITION_MS;

const BORDER_SIZE = 2;
const CONTAINER_SIZE = grid.unitless * 4.5;

interface Props {
    expanded: boolean;
}

export const ExpandingBackground = styled.div`
    width: ${CONTAINER_SIZE}px;
    height: ${CONTAINER_SIZE}px;
    top: 0;
    left: 0;
    position: absolute;
    border-radius: 50%;
    background-color: ${token('elevation.surface', colors.N0)};
    transition: background-color ${BACKGROUND_OPEN_TRANSITION_MS}ms, transform ${TRANSFORM_OPEN_TRANSITION_MS}ms;
    transition-timing-function: ease-in;
    z-index: ${zIndex.expanderBackground};
    border: ${BORDER_SIZE}px solid ${token('color.border.inverse', colors.N0)};
    box-sizing: border-box;

    ${bp.onlyXXSmall.css`
        transform: scale(25);
    `};

    ${bp.fromXXSmall.css`
        transform: scale(35);
    `};

    ${bp.fromXSmall.css`
        transform: scale(50);
    `};

    ${bp.fromSmall.css`
        transform: scale(55);
    `};

    ${bp.fromMedium.css`
        transform: scale(75);
    `};

    ${bp.fromLarge.css`
        transform: scale(85);
    `};

    ${bp.fromXLarge.css`
        transform: scale(95);
    `};

    ${bp.fromXXLarge.css`
        transform: scale(170);
    `};

    ${(props: Props) =>
        props.expanded
            ? ''
            : css`
                  /* stylelint-disable-next-line no-duplicate-selectors */
                  & {
                      background-color: ${token('elevation.surface.overlay', colors.N30)};
                      transition: background-color ${BACKGROUND_CLOSE_TRANSITION_MS}ms,
                          transform ${TRANSFORM_CLOSE_TRANSITION_MS}ms;
                      transform: none;
                  }
              `};

    /* This is emulating the hover behaviour of the @atlaskit/avatar component. If it changes, change this too. */
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: 50%;
        background-color: ${token('elevation.surface.overlay.hovered', colors.N200A)};
        opacity: 0;
        transition: opacity 200ms;
        pointer-events: none;
        box-sizing: border-box;
    }
`;

export const Container = styled.div`
    position: relative;
    width: ${CONTAINER_SIZE}px;
    height: ${CONTAINER_SIZE}px;
    border-radius: 50%;
    box-sizing: border-box;
    z-index: ${zIndex.expanderButtonContainer};

    ${(props: Props) =>
        props.expanded
            ? ''
            : css`
                  /* This is emulating the on click behaviour of the @atlaskit/avatar component. If it changes, change this too. */
                  &:active {
                      ${ButtonContentContainer}, ${ExpandingBackground} {
                          transform: scale(0.9);

                          &::after {
                              /* This ensures the background color change sticks when the element is active. */
                              opacity: 0.6;
                          }
                      }
                  }

                  &:hover {
                      /* We want to duplicate this selector as it should active on hover of Container, not ExpandingBackground. */
                      /* stylelint-disable-next-line no-duplicate-selectors */
                      ${ExpandingBackground} {
                          /* This is emulating the hover behaviour of the @atlaskit/avatar component. If it changes, change this too. */
                          &::after {
                              opacity: 0.6;
                          }
                      }
                  }
              `};
`;

type HTMLButtonProps = React.ComponentProps<typeof HtmlButton>;

export const Button = styled<Props & HTMLButtonProps>(({ expanded, ...props }) => <HtmlButton {...props} />)`
    ${mixins.focusRing.default};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
    color: ${token('color.text', 'initial')};
    border: none;
    cursor: pointer;
    z-index: ${zIndex.expanderButton};
    padding: 0;
    opacity: ${(props) => (props.expanded ? 0 : 1)};
    transition: opacity ${BUTTON_OPACITY_TRANSITION_MS}ms;
    transition-delay: ${(props) => (props.expanded ? 'inherit' : `${BUTTON_TRANSITION_DELAY_MS}ms`)};
    border-radius: 50%;
    box-sizing: border-box;
`;

export const ButtonContentContainer = styled.span`
    ${mixins.flexCenter};
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
`;
